/** Shared option sets for onboarding (aligned with UserSettings where applicable). */
import {
  codedOptionDisplays,
  ETHNICITY_OPTIONS,
  FAITH_OPTIONS,
  GENDER_OPTIONS,
  SEXUALITY_OPTIONS,
  PROVIDER_SPECIALTIES,
  THERAPY_TYPES,
} from '@unify/fhir-model/provider-profile-datasets';

export const LANGUAGES_OPTIONS = [
  'Arabic', 'Armenian', 'Cantonese', 'English', 'Farsi', 'French', 'German', 'Hebrew',
  'Hindi', 'Korean', 'Mandarin', 'Russian', 'Spanish', 'Tagalog (incl. Filipino)', 'Vietnamese',
];

export const GENDER_IDENTITY_OPTIONS = codedOptionDisplays(GENDER_OPTIONS);
export const LGBTQIA_OPTIONS = [...codedOptionDisplays(SEXUALITY_OPTIONS), 'Queer'];
export const RELIGION_OPTIONS = codedOptionDisplays(FAITH_OPTIONS);
export const RACE_ETHNICITY_OPTIONS = codedOptionDisplays(ETHNICITY_OPTIONS);
export const AGE_GROUP_OPTIONS = [
  'Children (10 years and younger)', 'Adults (20 to 64 years)',
  'Adolescents (11 to 19 years)', 'Elderly (65+ years)',
];
export const CLINICAL_SPECIALTIES_OPTIONS = codedOptionDisplays(PROVIDER_SPECIALTIES);

export const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
] as const;

export const US_STATES = US_STATE_CODES.map((s) => ({ value: s, label: s }));

/** Match typed text to a US state/territory code (e.g. "ca" → "CA"). */
export function resolveUsStateCode(input: string): string | null {
  const upper = input.trim().toUpperCase();
  return (US_STATE_CODES as readonly string[]).includes(upper) ? upper : null;
}

/** License type options (shared by onboarding and UserSettings Clinical Info). */
export const LICENSE_TYPES = [
  { value: 'lcsw', label: 'Licensed Clinical Social Worker' },
  { value: 'lpc', label: 'Licensed Professional Counselor' },
  { value: 'lpcc', label: 'Licensed Professional Clinical Counselor' },
  { value: 'lmft', label: 'Licensed Marriage & Family Therapist' },
  { value: 'clinical-psychologist', label: 'Clinical Psychologist' },
  { value: 'psychiatrist', label: 'Psychiatrist' },
  { value: 'psychiatric-np', label: 'Psychiatric Nurse Practitioner' },
  { value: 'other', label: 'Other' },
];

export const CASELOAD_PREFERENCE_OPTIONS = [
  '5-10 sessions/week',
  '10-15 sessions/week',
  '15-20 sessions/week',
  '20-25 sessions/week',
  '25-30 sessions/week',
  '30-35 sessions/week',
  '35+ sessions/week',
];

export const TRAINING_TYPE_OPTIONS = [
  'Telehealth Training',
  'Law and Ethics Certificate',
  'Supervisory CE',
  'Gender-Affirming Care',
  'Fraud, Waste & Abuse',
  'Other',
];

export const TREATMENT_MODALITIES_OPTIONS = [...codedOptionDisplays(THERAPY_TYPES), 'Other'];

/** Common NUCC taxonomy codes for mental health rendering providers (837P billing). */
export const NUCC_TAXONOMY_OPTIONS = [
  { value: '103T00000X', label: '103T00000X — Psychologist' },
  { value: '1041C0700X', label: '1041C0700X — Clinical Social Worker' },
  { value: '101YM0800X', label: '101YM0800X — Counselor' },
  { value: '106H00000X', label: '106H00000X — Marriage & Family Therapist' },
  { value: '163WP0800X', label: '163WP0800X — Psychiatric/Mental Health Nurse Practitioner' },
  { value: '2084P0800X', label: '2084P0800X — Psychiatry Physician' },
] as const;
